import { useNavigate, useParams } from "react-router-dom";
import { SearchX } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DeleteConfirmButton } from "@/components/delete-confirm-button";
import { DetailList, DetailRow } from "@/components/detail-list";
import { EntityButton } from "@/components/entity-button";
import { EmptyState } from "@/components/empty-state";
import { DetailSkeleton } from "@/components/loading-skeletons";
import { PageHeader, PageTitle } from "@/components/layout/page-header";
import { useApiKeysQuery, useDeleteApiKeyMutation } from "@/hooks/use-api-keys";
import { useServicesQuery } from "@/hooks/use-services";
import { formatDateTime } from "@/lib/utils";

const BREADCRUMBS = [{ label: "API Keys", to: "/settings/api-keys" }];

export function ApiKeyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: apiKeys, isLoading, isFetching, error, refetch } = useApiKeysQuery();
  const { data: services } = useServicesQuery();
  const deleteMutation = useDeleteApiKeyMutation();

  const apiKey = apiKeys?.find((k) => k.id === id);

  const serviceName = apiKey?.service_id
    ? services?.find((s) => s.id === apiKey.service_id)?.title || apiKey.service_id
    : "All services";

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id!);
      navigate("/settings/api-keys");
    } catch {
      // Error handled by mutation state
    }
  };

  // A key created a moment ago isn't in the cached list yet, so wait for the
  // refetch (the create invalidated the list) before calling it missing.
  if (isLoading || (!apiKey && isFetching)) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="API Key" />
        <DetailSkeleton />
      </div>
    );
  }

  if (error || !apiKey) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <PageHeader breadcrumbs={BREADCRUMBS} title="API Key" />
        <EmptyState
          icon={SearchX}
          title="API Key Not Found"
          description="The API key you are looking for does not exist."
        >
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={apiKey.name}
        actions={
          <>
            <EntityButton entityName="API Key" mode="edit" to={`/settings/api-keys/${id}/edit`} />
            <DeleteConfirmButton
              entityName="API Key"
              name={apiKey.name}
              onConfirm={handleDelete}
              mutation={deleteMutation}
            />
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <PageTitle title={apiKey.name} />

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailList>
              <DetailRow label="Name">{apiKey.name}</DetailRow>
              <DetailRow label="Service">{serviceName}</DetailRow>
              <DetailRow label="API Key">
                <span className="font-mono">{apiKey.api_key}</span>
              </DetailRow>
              <DetailRow label="Created">{formatDateTime(apiKey.created_at)}</DetailRow>
            </DetailList>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
